import { Fragment, useEffect, useMemo, useRef } from 'react'
import type { Conversation, SlackMessage } from '@assembly/slack'
import { useActions } from '../data/actions'
import { useBookmarks, useConversation, useConversationName, useHistory, useMembers, usePresence, useReplies, useUser } from '../data/hooks'
import { useMe } from '../data/workspace'
import { inTimeline } from '../data/cache'
import { useUi } from '../state/ui'
import { dayKey, formatDay, formatShortDate } from '../format'
import { Composer } from './Composer'
import { Icon } from './Icon'
import { Message, shouldGroup } from './Message'
import { ExternalLink, Mrkdwn } from './MessageContent'
import { Emoji, IconButton, Spinner, UserAvatar, useIsNarrow } from './common'

export function ConversationIcon({ conv, size = 16 }: { conv?: Conversation; size?: number }) {
  if (!conv) return <Icon name="hash" size={size} />
  if (conv.is_im) return <DmIcon user={conv.user} size={size} />
  if (conv.is_mpim) return <span className="mpim-count" style={{ width: size + 2, height: size + 2, fontSize: size * 0.62 }}>{Math.max(2, (conv.name?.split('--').length ?? 3) - 1)}</span>
  return <Icon name={conv.is_private ? 'lock' : 'hash'} size={size} />
}

function DmIcon({ user, size }: { user?: string; size: number }) {
  const { data: presence } = usePresence(user)
  return (
    <span className="dm-icon">
      <UserAvatar id={user} size={size + 4} />
      <span className={`presence ${presence === 'active' ? 'active' : ''}`} />
    </span>
  )
}

function ChannelHeader({ conv }: { conv: Conversation }) {
  const name = useConversationName()
  const openPanel = useUi((s) => s.openPanel)
  const goHome = useUi((s) => s.goHome)
  const panel = useUi((s) => s.panel)
  const narrow = useIsNarrow()
  const dmUser = useUser(conv.is_im ? conv.user : undefined)
  const { data: bookmarks } = useBookmarks(conv.is_im || conv.is_mpim ? undefined : conv.id)
  const { data: members } = useMembers(conv.is_channel || conv.is_group ? conv.id : undefined)
  const detailsOpen = panel?.type === 'details'
  const toggleDetails = (tab?: 'about' | 'members' | 'pins') => openPanel(detailsOpen && !tab ? null : { type: 'details', channel: conv.id, tab })

  return (
    <header className="channel-header">
      <div className="channel-header-main">
        {narrow && <IconButton icon="chevronLeft" label="Back" onClick={goHome} className="back-button" />}
        <button type="button" className="channel-title" onClick={() => (conv.is_im && conv.user ? openPanel({ type: 'profile', user: conv.user }) : toggleDetails('about'))}>
          <ConversationIcon conv={conv} size={16} />
          <span>{name(conv)}</span>
          {dmUser?.profile?.status_emoji && <Emoji name={dmUser.profile.status_emoji.replace(/:/g, '')} />}
          <Icon name="chevronDown" size={14} />
        </button>
        {!narrow && conv.topic?.value && (
          <span className="channel-topic" title={conv.topic.value}>
            <Mrkdwn text={conv.topic.value} inline />
          </span>
        )}
        <span className="header-spacer" />
        {(conv.is_channel || conv.is_group || conv.is_mpim) && members && (
          <button type="button" className="member-count" onClick={() => openPanel({ type: 'details', channel: conv.id, tab: 'members' })} title="Members">
            <Icon name="users" size={16} />
            <span>{members.length}</span>
          </button>
        )}
        <IconButton icon="pin" label="Pinned messages" onClick={() => openPanel({ type: 'details', channel: conv.id, tab: 'pins' })} />
        <IconButton icon="info" label="Details" active={detailsOpen} onClick={() => toggleDetails()} />
      </div>
      {bookmarks && bookmarks.length > 0 && (
        <nav className="bookmarks-bar">
          {bookmarks.map((b) => (
            <ExternalLink key={b.id} href={b.link}>
              <span className="bookmark">
                {b.emoji ? <Emoji name={b.emoji.replace(/:/g, '')} /> : b.icon_url ? <img src={b.icon_url} alt="" /> : <Icon name="link" size={13} />}
                {b.title}
              </span>
            </ExternalLink>
          ))}
          <button type="button" className="bookmark add" onClick={() => useUi.getState().openModal({ type: 'addBookmark', channel: conv.id })}>
            <Icon name="plus" size={13} /> Add a bookmark
          </button>
        </nav>
      )}
    </header>
  )
}

function ChannelIntro({ conv }: { conv: Conversation }) {
  const name = useConversationName()
  const creator = useUser(conv.creator)
  const openModal = useUi((s) => s.openModal)
  const openPanel = useUi((s) => s.openPanel)
  if (conv.is_im) {
    return (
      <div className="channel-intro">
        <UserAvatar id={conv.user} size={72} />
        <h2>{name(conv)}</h2>
        <p className="muted">This is the very beginning of your direct message history with {name(conv)}.</p>
        <button type="button" className="button" onClick={() => conv.user && openPanel({ type: 'profile', user: conv.user })}>
          View profile
        </button>
      </div>
    )
  }
  return (
    <div className="channel-intro">
      <h2>
        <ConversationIcon conv={conv} size={26} /> {name(conv)}
      </h2>
      <p className="muted">
        {creator ? <>Created by @{creator.profile.display_name || creator.real_name}</> : 'This is the very beginning of this channel'}
        {conv.created ? <> on {formatShortDate(conv.created)}</> : null}.{' '}
        {conv.purpose?.value && <Mrkdwn text={conv.purpose.value} inline />}
      </p>
      {!conv.is_mpim && (
        <div className="intro-actions">
          <button type="button" className="button" onClick={() => openModal({ type: 'invite', channel: conv.id })}>
            <Icon name="userPlus" size={16} /> Add people
          </button>
          <button type="button" className="button" onClick={() => openPanel({ type: 'details', channel: conv.id, tab: 'about' })}>
            <Icon name="pencil" size={16} /> Edit description
          </button>
        </div>
      )}
    </div>
  )
}

/**
 * Reverse-ordered timeline: the scroller uses flex-direction: column-reverse so it
 * starts pinned to the newest message and stays there as messages arrive.
 */
export function MessageList({ channel, conv }: { channel: string; conv?: Conversation }) {
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, error } = useHistory(channel)
  const highlight = useUi((s) => (s.highlight?.channel === channel ? s.highlight.ts : null))
  const actions = useActions()
  const sentinel = useRef<HTMLDivElement>(null)
  const scroller = useRef<HTMLDivElement>(null)

  const messages = useMemo(() => (data?.pages.flatMap((p) => p.messages) ?? []).filter((m) => inTimeline(m) && !m.hidden), [data])

  // Load older messages when the top sentinel scrolls into view.
  useEffect(() => {
    const el = sentinel.current
    if (!el) return
    const io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) fetchNextPage()
    }, { root: scroller.current, rootMargin: '600px 0px 0px 0px' })
    io.observe(el)
    return () => io.disconnect()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  // Jump to a highlighted message, paging back to find it if needed.
  const pagesSearched = useRef(0)
  useEffect(() => {
    if (!highlight || isLoading) return
    const el = scroller.current?.querySelector(`[data-ts="${CSS.escape(highlight)}"]`)
    if (el) {
      el.scrollIntoView({ block: 'center' })
      pagesSearched.current = 0
    } else if (hasNextPage && !isFetchingNextPage && pagesSearched.current++ < 20) {
      fetchNextPage()
    }
  }, [highlight, messages, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage])

  // Mark the conversation read as new messages appear while it's open.
  const latest = messages.find((m) => !m.pending)?.ts
  useEffect(() => {
    if (!latest) return
    const t = setTimeout(() => document.visibilityState === 'visible' && actions.markRead(channel, latest), 800)
    return () => clearTimeout(t)
  }, [latest, channel, actions])

  if (error) return <div className="empty-state">Couldn’t load messages: {(error as Error).message}</div>

  return (
    <div className="message-scroller" ref={scroller}>
      <div className="message-list">
        {isLoading && (
          <div className="loading-row">
            <Spinner />
          </div>
        )}
        {messages.map((m, i) => {
          const older: SlackMessage | undefined = messages[i + 1]
          const newDay = !older || dayKey(older.ts) !== dayKey(m.ts)
          return (
            <Fragment key={m.ts}>
              <Message message={m} channel={channel} grouped={!newDay && shouldGroup(m, older)} highlighted={m.ts === highlight} />
              {newDay && (
                <div className="day-divider">
                  <span>{formatDay(m.ts)}</span>
                </div>
              )}
            </Fragment>
          )
        })}
        {!isLoading && !hasNextPage && conv && <ChannelIntro conv={conv} />}
        {isFetchingNextPage && (
          <div className="loading-row">
            <Spinner />
          </div>
        )}
        <div ref={sentinel} className="sentinel" />
      </div>
    </div>
  )
}

export function ChannelView({ channel }: { channel: string }) {
  const { data: conv, isLoading, error } = useConversation(channel)
  const me = useMe()
  const setEditing = useUi((s) => s.setEditing)
  const { data } = useHistory(channel)

  const editLast = () => {
    const mine = data?.pages.flatMap((p) => p.messages).find((m) => m.user === me && !m.pending && inTimeline(m))
    if (mine) setEditing({ channel, ts: mine.ts })
  }

  if (error) return <div className="empty-state">Couldn’t open this conversation.</div>
  if (!conv && isLoading) {
    return (
      <div className="empty-state">
        <Spinner />
      </div>
    )
  }

  return (
    <section className="channel-view">
      {conv && <ChannelHeader conv={conv} />}
      <MessageList channel={channel} conv={conv} />
      <div className="composer-wrap">
        <Composer key={channel} channel={channel} onEditLast={editLast} />
      </div>
    </section>
  )
}

export function ThreadPanel({ channel, ts }: { channel: string; ts: string }) {
  const { data: conv } = useConversation(channel)
  const name = useConversationName()
  const openPanel = useUi((s) => s.openPanel)
  const highlight = useUi((s) => (s.highlight?.channel === channel ? s.highlight.ts : null))
  const { data: replies, isLoading } = useReplies(channel, ts)
  const bottom = useRef<HTMLDivElement>(null)

  const count = replies?.length ?? 0
  useEffect(() => {
    bottom.current?.scrollIntoView({ block: 'end' })
  }, [count])

  const [parent, ...rest] = replies ?? []
  return (
    <aside className="side-panel thread-panel">
      <header className="panel-header">
        <div>
          <h3>Thread</h3>
          {conv && <span className="muted">{conv.is_im ? name(conv) : `#${name(conv)}`}</span>}
        </div>
        <IconButton icon="x" label="Close thread" onClick={() => openPanel(null)} />
      </header>
      <div className="panel-scroll">
        {isLoading && (
          <div className="loading-row">
            <Spinner />
          </div>
        )}
        {parent && <Message message={parent} channel={channel} inThread highlighted={parent.ts === highlight} />}
        {rest.length > 0 && (
          <div className="thread-divider">
            <span>
              {rest.length} {rest.length === 1 ? 'reply' : 'replies'}
            </span>
          </div>
        )}
        {rest.map((m, i) => (
          <Message key={m.ts} message={m} channel={channel} inThread grouped={shouldGroup(m, rest[i - 1])} highlighted={m.ts === highlight} />
        ))}
        <div ref={bottom} />
      </div>
      <div className="composer-wrap">
        <Composer key={`${channel}:${ts}`} channel={channel} threadTs={ts} />
      </div>
    </aside>
  )
}
